import { NameInnerRenderer } from "./SpanDetails/NameInnerRenderer";
import { CompactWaterfall } from "./SpanDetails/Waterfall";
import type { TraceDetailState } from "./hooks/useTraceDetailState";
import type { SpanTableRow } from "./hooks/useTraceTable";

import { SimpleTable } from "@/components/ui/ag-grid-table";
import type { SpanNode } from "@/types/spans";

const calculateWaterfallPosition = (
  spanNode: SpanNode,
  traceStartTime: number,
  traceDuration: number,
) => {
  const safeTraceDuration = traceDuration || 1;
  const spanStartTime = new Date(spanNode.start).getTime();

  if (spanNode.end === null) {
    const startOffset = spanStartTime - traceStartTime;
    const startPercentage = (startOffset / safeTraceDuration) * 100;

    return {
      left: `${Math.max(0, startPercentage)}%`,
      width: "100%",
      isRunning: true,
    };
  }

  const spanEndTime = new Date(spanNode.end).getTime();
  const spanDuration = spanEndTime - spanStartTime;
  const startOffset = spanStartTime - traceStartTime;
  const startPercentage = (startOffset / safeTraceDuration) * 100;
  const widthPercentage = (spanDuration / safeTraceDuration) * 100;

  return {
    left: `${Math.max(0, startPercentage)}%`,
    width: `${Math.max(0.5, widthPercentage)}%`,
    isRunning: false,
  };
};

interface TraceSpanHierarchyProps {
  flattenedSpans: TraceDetailState["flattenedSpans"];
  isHighlighted: TraceDetailState["isHighlighted"];
  setSelectedSpan: TraceDetailState["setSelectedSpan"];
  setTimeRange: TraceDetailState["setTimeRange"];
  spanStatuses: TraceDetailState["spanStatuses"];
  spanTree: TraceDetailState["spanTree"];
  table: TraceDetailState["table"];
  timeRange: TraceDetailState["timeRange"];
}

const TraceSpanHierarchy = ({
  flattenedSpans,
  isHighlighted,
  setSelectedSpan,
  setTimeRange,
  spanStatuses,
  spanTree,
  table,
  timeRange,
}: TraceSpanHierarchyProps) => {
  if (!spanTree?.length) {
    return <p className="p-4 text-gray-500">No spans found for this trace.</p>;
  }

  const rootSpanHasEndTime = spanTree[0]?.end !== null;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
        <h3 className="text-sm font-semibold text-gray-700">Span Hierarchy</h3>
      </div>
      {rootSpanHasEndTime && (
        <CompactWaterfall
          spans={flattenedSpans}
          spanStatuses={spanStatuses}
          timeRange={timeRange}
          onTimeRangeChange={setTimeRange}
          calculateWaterfallPosition={calculateWaterfallPosition}
          onSpanClick={setSelectedSpan}
        />
      )}
      <div className="flex-1 overflow-hidden">
        <SimpleTable<SpanTableRow>
          data={table.filteredTableData}
          columns={table.columns}
          getApiRef={table.setGridApi}
          enablePagination={false}
          suppressHeaderMenuButton
          enableSorting={false}
          enableFiltering={false}
          enableResizing
          hoverable
          onRowClick={row => {
            setSelectedSpan(row as unknown as SpanNode);
          }}
          getRowId={(params: { data: SpanTableRow }): string => params.data.span_id}
          isHighlighted={isHighlighted}
          rowHeight={32}
          licenseKey={import.meta.env.REACT_APP_AG_GRID_LICENSE_KEY}
          treeData
          getDataPath={(rowData: SpanTableRow) => rowData.orgHierarchy}
          autoGroupColumnDef={{
            headerName: "Name",
            suppressHeaderMenuButton: true,
            width: 300,
            cellRendererParams: {
              innerRenderer: NameInnerRenderer,
            },
          }}
          gridOptions={table.gridOptions}
          className="h-full [&_.ag-root-wrapper]:border-none"
        />
      </div>
    </div>
  );
};

export default TraceSpanHierarchy;
